import {isSecondaryManual} from './componentManualLauncherAssembly'
// © 2026 Holmes Computer Consultants. All rights reserved. -- www.holmesconsultants.com
//
// The balls (M3-08).
//
// **`Ball.z` is the centre height, not the height above the floor.** A ball resting on the
// playfield has `z = BALL_RADIUS`; a ball on a habitrail has `z = wireZ + BALL_RADIUS`, set by
// the wire itself. So the mesh goes at `z` and nothing is added to it. The first build of the
// 3-D view added a radius, which put every ball a full 27 mm diameter above the wood — high
// enough to be wrong and low enough that from the player's camera, at a glancing angle down a
// metre of playfield, it simply reads as a bright ball.
//
// The orientation comes from the solver's own quaternion rather than from a rotation derived
// from velocity. That is the difference between a ball that skids and a ball that rolls
// (PH-A15), and it is a real property of the game: a ball leaving a flipper with backspin
// behaves differently from one that does not, and now it looks different too.

import {
  CircleGeometry,
  Group,
  Mesh,
  SphereGeometry,
  MeshStandardMaterial,
  type BufferGeometry,
  type Material,
} from 'three'
import type { World } from '@core/sim/World'
import type {MachineDevice} from '@core/machine'
import {isScoopKicker,heldScoopBallOffset} from './componentKickerPresentation'
import {heldAutoLauncherBallOffset} from './autoLauncherPresentation'
import { BALL_RADIUS } from '@core/sim/constants'
import { lerp } from '../engine/SimulationDriver'

interface BallFactory {
 readonly geometry:(radius:number)=>readonly BufferGeometry[]
 readonly materials:()=>readonly Material[]
}
let ballFactory:BallFactory|undefined
/** Prepared with the deferred component kit; isolated arithmetic fixtures retain their fallback. */
export function registerBallFactory(factory:BallFactory):void {ballFactory=factory}
export function createBallMaterials():readonly Material[] {
 return ballFactory?.materials()??[
  new MeshStandardMaterial({name:'ball.polishedSteel',color:0xcbd0d4,roughness:.14,metalness:1}),
  new MeshStandardMaterial({name:'ball.rotationWitness',color:0x8e979f,roughness:.34,metalness:.92}),
 ]
}

export interface BuiltBalls {
  readonly group: Group
  /** Shared pool geometry, disposed once. Materials belong to the caller. */
  readonly geometries: readonly BufferGeometry[]
  /** Pose every ball for one frame, interpolating between the last two simulation states. */
  readonly animate: (alpha: number) => void
}

/**
 * One mesh per ball the world can hold, whether or not it is in play.
 *
 * All of them, built once: a machine with a six-ball multiball would otherwise have to create
 * geometry mid-game, at the exact moment it is least able to afford a hitch.
 */
export function buildBalls(
  world: World,
  material: Material,
  poleMaterial: Material,
  segments: number,
  definitions:readonly MachineDevice[]=[],
): BuiltBalls {
  const group = new Group()
  group.name = 'balls'
  const scoops=new Set(definitions.filter(d=>d.kind==='kicker'&&isScoopKicker(d)).map(d=>d.id))
  const scoopIndices=new Set(world.devices.filter(d=>scoops.has(d.id)).map(d=>d.index))
  const automatic=new Set(definitions.filter(d=>d.kind==='autoPlunger'||isSecondaryManual(d,definitions)).map(d=>d.id)),automaticIndices=new Set(world.devices.filter(d=>automatic.has(d.id)).map(d=>d.index))
  const geometries: BufferGeometry[] = []
  const meshes: Mesh[] = []

  const surfaces=ballFactory?.geometry(BALL_RADIUS)??[
    new SphereGeometry(BALL_RADIUS,segments,Math.max(4,segments/2)),
    new CircleGeometry(BALL_RADIUS*.32,12),
  ]
  geometries.push(...surfaces)
  for(let i=0;i<world.balls.length;i++){
    const mesh=new Mesh(surfaces[0],material);mesh.name=`ball${i}`;mesh.visible=false;group.add(mesh);meshes.push(mesh)
    if(surfaces.length>1){
      const witness=new Mesh(surfaces[1],poleMaterial);witness.name=`ball${i}.surfaceWitness`
      if(ballFactory===undefined)witness.position.z=BALL_RADIUS*.955
      mesh.add(witness)
    }
  }

  const animate = (alpha: number): void => {
    for (let i = 0; i < meshes.length; i++) {
      const ball = world.balls[i]
      const mesh = meshes[i]
      if (ball === undefined || mesh === undefined) continue
      // Held balls are drawn too. `heldBy` means a device owns the ball's motion, not that
      // the ball has left the machine: the plunger holds one for the whole time a player is
      // drawing back, and hiding it makes the ball vanish at the start of every single ball.
      // A scoop is the arguable case, and a ball visible in a scoop mouth is far less wrong
      // than a ball that disappears while you are aiming it.
      mesh.visible = ball.active
      if (!mesh.visible) continue
      mesh.position.set(
        lerp(ball.prevX, ball.x, alpha),
        lerp(ball.prevY, ball.y, alpha),
        lerp(ball.prevZ, ball.z, alpha)+heldScoopBallOffset(ball.heldBy>=0&&scoopIndices.has(ball.heldBy))+heldAutoLauncherBallOffset(ball.heldBy>=0&&automaticIndices.has(ball.heldBy)),
      )
      mesh.quaternion.set(ball.qx, ball.qy, ball.qz, ball.qw)
    }
  }

  return { group, geometries, animate }
}
